/**
 * Typed API failures.
 *
 * The server returns a structured envelope (spec §8) — `{code, message,
 * details, trace_id}`. Callers need more than a status code: a 409 carries
 * `details.server_version`, which is what makes the phone's conflict
 * resolution possible without a second round trip.
 */
import { ApiErrorBody } from '@kapture/types';

export class ApiError extends Error {
  readonly status: number;
  /** Server error code, e.g. `version_conflict`. Null if the body wasn't ours. */
  readonly code: string | null;
  readonly details: Record<string, unknown> | null;
  readonly traceId: string | null;

  constructor(status: number, body: ApiErrorBody | null, fallbackMessage?: string) {
    super(body?.message ?? fallbackMessage ?? `Request failed: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code ?? null;
    this.details = (body?.details as Record<string, unknown>) ?? null;
    this.traceId = body?.trace_id ?? null;
  }

  /**
   * Version the server holds for this row, from a 409. Used to re-base a
   * keep-local resolution instead of retrying into the same conflict forever.
   */
  get serverVersion(): number | null {
    const value = this.details?.server_version;
    return typeof value === 'number' ? value : null;
  }

  /** Transient failures worth another attempt; anything else needs a human. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

/** Network-level failure — no response at all. Always worth retrying. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'NetworkError';
  }
}

/**
 * The request ran out of time and we cut it off ourselves.
 *
 * Deliberately a `NetworkError`, because that is what it is: no response
 * arrived, and the queue's rule that a response-less failure is always worth
 * another attempt is exactly right here. Subclassing rather than reusing
 * `NetworkError` directly buys two things — an error message a person can act
 * on ("timed out after 55s" rather than the platform's own wording, which on
 * an aborted `fetch` is the uninformative "The operation was aborted") and a
 * type the retry policy could one day treat differently without having to
 * pattern-match on a string.
 *
 * A timeout is not proof the server did nothing. The request may have been
 * received and applied; we simply stopped listening. That is safe here only
 * because every mutating call carries an `Idempotency-Key`, so the retry is
 * served the original outcome rather than writing twice — without that, a
 * timeout on an upload would be a duplicate waiting to happen.
 */
export class TimeoutError extends NetworkError {
  /** The budget that elapsed, in ms — what the message quotes. */
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(new Error(`request timed out after ${Math.round(timeoutMs / 1000)}s`));
    this.name = 'TimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/**
 * Parse an error envelope out of a body that has already been read.
 *
 * The client reads the body as text (once — a `Response` body is a stream and
 * cannot be consumed twice) so that the read itself sits inside the request's
 * timeout. That leaves it holding a string rather than a `Response`, hence
 * this shape; `parseErrorBody` below is the same logic for the callers that
 * still have the response in hand.
 */
export function parseErrorText(text: string): ApiErrorBody | null {
  if (!text) return null;
  try {
    const parsed = ApiErrorBody.safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : null;
  } catch {
    // Not JSON — an HTML error page from a proxy, most likely. The status
    // code is all we get.
    return null;
  }
}

export async function parseErrorBody(res: Response): Promise<ApiErrorBody | null> {
  try {
    return parseErrorText(await res.text());
  } catch {
    // No body at all.
    return null;
  }
}
